import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { fetchPatient, updatePatient } from "../../../Redux/PatientSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AddAllergyModal from "./AddAllergyModal";
import EditAllergyModal from "./EditAllergyModal";
import { fetchPatientAllergies } from "../../../Redux/AllergySlice";
import { InfinitySpin } from "react-loader-spinner";
import { hasPermission } from "../../../Redux/permissionSlice";
import { selectRoles } from "../../../Redux/AuthSlice";
import Table from "../../dataUi/Table";
import { BsPencil, BsX } from "react-icons/bs";
import Breadcrumb from "../../dataUi/Breadcrumb";
import dayjs from 'dayjs';
import { fetchPatientPrescriptions } from '../../../Redux/PrescriptionSlice';

const ViewPatient = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const dispatch = useDispatch();
  const { patientDetail, loading } = useSelector((state) => state.patients);
  const { allergies } = useSelector((state) => state.allergy);
  const { id: userId, branch_id: branchId } = useSelector((state) => state.auth) || {};
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);

  const openEditModal = (allergyToEdit) => {
    setSelectedAllergy(allergyToEdit);
    setIsEditModalOpen(true);
  };

  const ID_TYPES = [
    { value: "passport", label: "Passport" },
    { value: "drivers_license", label: "Driver's License" },
    { value: "ghana_card", label: "Ghana Card" },
    { value: "nhis", label: "NHIS" },
    { value: "state_id", label: "State ID" },
  ];

  const GENDER_OPTIONS = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
  ];
  const [isEditing, setIsEditing] = useState(false);
  const [patient, setPatient] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    idType: "",
    idNumber: "",
    gender: "",
    phoneNumber: "",
    address: "",
  });
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchPatient({ patientId })).unwrap();
      } catch (error) {
        console.error("Error fetching patient details:", error);
        toast.error("Failed to fetch patient details");
      }
    };

    fetchData();
  }, [dispatch, patientId]);

  // Update patient state when patientDetail changes
  useEffect(() => {
    if (patientDetail) {
      setPatient({
        firstName: patientDetail.first_name || "",
        lastName: patientDetail.last_name || "",
        dateOfBirth: patientDetail.date_of_birth || "",
        idType: patientDetail.id_type || "",
        idNumber: patientDetail.id_number || "",
        gender: patientDetail.gender || "",
        phoneNumber: patientDetail.phone_number || "",
        address: patientDetail.address || "",
      });
    }
  }, [patientDetail]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchPatientAllergies({ patientId })).unwrap();
      } catch (error) {
        console.error("Error fetching patient details:", error);
        toast.error("Failed to fetch patient details");
      }
    };

    fetchData();
  }, [dispatch, patientId]);

  const handleInputChange = (field, value) => {
    setPatient({ ...patient, [field]: value });
  };

  const handleSubmit = async () => {
    try {
      await dispatch(
        updatePatient({
          patientId,
          patientData: {
            first_name: patient.firstName,
            last_name: patient.lastName,
            date_of_birth: patient.dateOfBirth,
            id_type: patient.idType,
            id_number: patient.idNumber,
            gender: patient.gender,
            phone_number: patient.phoneNumber,
            address: patient.address,
          },
        })
      ).unwrap();
      setIsEditing(false);
      toast.success("Patient details updated successfully");
    } catch (error) {
      console.error("Error updating patient details:", error);
      toast.error("Failed to update patient details");
    }
  }; // Removed unused allergy handling functions since they are now handled by Redux

  const selectedRoles = useSelector(selectRoles);
  const roles = Array.isArray(selectedRoles)
    ? selectedRoles
    : selectedRoles.split(",").map((role) => role.trim());

  // Add table configuration for allergies
  const allergyHeaders = [
    // {
    //   key: "no_known_allergies",
    //   label: "No Known Allergies",
    //   sortable: true,
    //   render: (value) => (value ? "Yes" : "No"),
    // },
    { key: "name", label: "Name", sortable: true },
    { key: "reaction", label: "Reaction", sortable: true },
    { key: "onset_date", label: "Onset Date", sortable: true },
  ];

  const allergyActions = [
    {
      label: "Edit",
      variant: "secondary",
      icon: <BsPencil />,
      onClick: (allergy) => openEditModal(allergy),
      show: hasPermission(roles, ["prescriptionWriter", "prescriptionAdmin"]),
      className: "bg-green-500 hover:bg-green-800 text-white"
    },
  ];

  // Show all allergies, including 'No Known Allergies' as a row
  // Format onset_date as MM/DD/YYYY for the table
  const allergyRows = (allergies || []).map(allergy => ({
    ...allergy,
    onset_date: allergy.onset_date ? dayjs(allergy.onset_date).format('MM/DD/YYYY') : '',
  }));

  // Debug: Log allergies and allergyRows
  console.log('Allergies from API:', allergies);
  console.log('Allergy rows for table:', allergyRows);

  const hasNoKnownAllergies = allergies?.some(
    (allergy) => allergy.no_known_allergies === true
  );

  // Fetch patient prescriptions
  useEffect(() => {
    const fetchPrescriptions = async () => {
      setPrescriptionsLoading(true);
      try {
        console.log('Auth data from Redux:', { userId, branchId });

        if (!userId || !branchId) {
          console.error('Missing required auth data:', { userId, branchId });
          return;
        }

        if (!patientId) {
          console.error('Missing patientId');
          return;
        }

        console.log('Fetching prescriptions with params:', { userId, branchId, patientId });

        const response = await dispatch(fetchPatientPrescriptions({
          userId,
          branchId,
          patientId
        })).unwrap();

        console.log('Prescription API Response:', response);

        // Set prescriptions directly since response is now an array
        setPrescriptions(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error fetching prescriptions:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
        toast.error('Failed to fetch prescriptions');
        setPrescriptions([]);
      } finally {
        setPrescriptionsLoading(false);
      }
    };

    console.log('Starting prescription fetch for patientId:', patientId);
    if (patientId) {
      fetchPrescriptions();
    } else {
      console.error('No patientId available');
    }
  }, [dispatch, patientId, userId, branchId]);

  // Add status configuration for prescriptions
  const prescriptionStatusConfig = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800'
    },
    preparing: {
      label: 'Preparing',
      className: 'bg-blue-100 text-blue-800'
    },
    is_ready: {
      label: 'Ready',
      className: 'bg-green-100 text-green-800'
    },
    picked_up: {
      label: 'Picked Up',
      className: 'bg-green-100 text-green-800'
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-red-100 text-red-800'
    }
  };

  // Add filter options for prescription status
  const prescriptionFilterOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Preparing', value: 'preparing' },
    { label: 'Ready', value: 'is_ready' },
    { label: 'Picked Up', value: 'picked_up' },
    { label: 'Cancelled', value: 'cancelled' }
  ];

  // Update prescription rows mapping to handle the data structure correctly
  const prescriptionRows = Array.isArray(prescriptions) ? prescriptions.map((prescription, index) => {
    console.log('Processing prescription:', prescription);
    const row = {
      id: prescription.id,
      number: index + 1,
      order_id: prescription.order_id,
      date_issued: prescription.date_issued ? dayjs(prescription.date_issued).format('MM/DD/YYYY') : '',
      medications: prescription.medications ? prescription.medications.map(med =>
        med.medication_name?.name || 'Unknown Medication'
      ).join(', ') : '',
      provider_name: prescription.provider ?
        `${prescription.provider.first_name || ''} ${prescription.provider.last_name || ''}`.trim() :
        'No Provider',
      provider_phone: prescription.provider?.phone_number || 'N/A',
      date_dispensed: prescription.date_issued ? dayjs(prescription.date_issued).format('MM/DD/YYYY') : 'Not Dispensed',
      status: prescription.status || 'pending'
    };
    console.log('Created row:', row);
    return row;
  }) : [];

  const prescriptionActions = [
    {
      label: 'View',
      variant: 'secondary',
      onClick: (prescription) => navigate(`/retail/prescriptions/view-prescription/${prescription.id}`, {
        state: { requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'] }
      }),
      show: true,
      className: "bg-green-500 hover:bg-green-800 text-white"
    }
  ];

  const handleRowClick = (prescription) => {
    navigate(`/retail/prescriptions/view-prescription/${prescription.id}`, {
      state: { requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'] }
    });
  };

  // Update prescription headers to include status
  const prescriptionHeaders = [
    { key: 'number', label: '#', sortable: true },
    { key: 'order_id', label: 'Order ID', sortable: true },
    { key: 'date_issued', label: 'Date', sortable: true },
    { key: 'medications', label: 'Medication Name(s)', sortable: false },
    { key: 'provider_name', label: 'Provider Name', sortable: false },
    { key: 'provider_phone', label: 'Provider Phone', sortable: false },
    { key: 'date_issued', label: 'Date Issued', sortable: true },
    { key: 'status', label: 'Status', sortable: true }
  ];

  console.log('Current prescriptions state:', prescriptions);

  if (loading) {
    return (
      <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
        <InfinitySpin color="#059212" loading={loading} size={40} />
      </div>
    );
  }

  const breadcrumbItems = [
    {
      label: "Prescriptions",
      onClick: () => navigate("/retail/prescriptions"),
    },
    {
      label: "Patients info",
      onClick: () => navigate(-1),
    },
    {
      label: "View patient",
    },
  ];

  return (
    <div className="absolute w-full ">
      <div className="ml-5 mr-10 font-lufga">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xl font-semibold lg:text-2xl text-slate-600">
              Patient Details
            </p>
            <Breadcrumb
              items={breadcrumbItems}
              className="flex items-center text-sm text-green-500 transition-all duration-300 hover:text-green-600"
            />
          </div>
          {/* {hasPermission(roles, ["prescriptionWriter", "prescriptionAdmin"]) && (
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="bg-green-500 text-white px-2 py-1.5 rounded hover:bg-green-600"
            >
              <BsPencil className="inline-block text-white" /> {" "}
              {isEditing ? "Cancel" : "Edit"}
            </button>
          )} */}
        </div>
        <div className="p-4 mb-4 border-b rounded-md shadow">
          <div className="flex justify-between">
            <h2 className="mb-2 text-lg font-medium text-green-700">Patient Info</h2>
            {/* {hasPermission(roles, ["prescriptionWriter", "prescriptionAdmin"]) && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="bg-green-500 text-white px-2 py-1.5 rounded hover:bg-green-600"
              >
                <BsPencil className="inline-block text-white" /> {" "}
                {isEditing ? "Cancel" : "Edit"}
              </button>
            )} */}
            {hasPermission(roles, ["prescriptionWriter", "prescriptionAdmin"]) && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`${isEditing ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white p-1 h-7 px-1.5 rounded-md text-sm shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                {isEditing ? (
                  <BsX className="inline-block text-xs text-white" />
                ) : (
                  <BsPencil className="inline-block text-xs text-white" />
                )} {" "}
                {isEditing ? "Cancel" : "Edit"}
              </button>
            )}

          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold">First Name{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <input
                  type="text"
                  value={patient.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-700">{patient.firstName || "N/A"}</p>
              )}
            </div>


            <div>
              <label className="block text-sm font-semibold">Last Name{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <input
                  type="text"
                  value={patient.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-700">{patient.lastName || "N/A"}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold">Date of Birth{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <input
                  type="date"
                  value={patient.dateOfBirth}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-700">{new Date(patient?.dateOfBirth).toLocaleDateString() || "N/A"}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold">Gender{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <select
                  value={patient.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Gender</option>
                  {GENDER_OPTIONS.map((gender) => (
                    <option key={gender.value} value={gender.value}>
                      {gender.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-700">
                  {GENDER_OPTIONS.find((gender) => gender.value === patient.gender)?.label || "N/A"}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold">Phone number{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <input
                  type="text"
                  value={patient.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-700">{patient.phoneNumber || "N/A"}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold">Address{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <input
                  type="text"
                  value={patient.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-700">{patient.address || "N/A"}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold">ID Type{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <select
                  value={patient.idType}
                  onChange={(e) => handleInputChange("idType", e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select ID Type</option>
                  {ID_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-700">
                  {ID_TYPES.find((type) => type.value === patient.idType)?.label || patient.idType || "N/A"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold">ID Number{isEditing && <span className="text-red-500">*</span>}</label>
              {isEditing ? (
                <input
                  type="text"
                  value={patient.idNumber}
                  onChange={(e) => handleInputChange("idNumber", e.target.value)}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-700">{patient.idNumber || "N/A"}</p>
              )}
            </div>
          </div>
          {isEditing && (
            <div className="flex justify-end mt-4">
              <button
                onClick={handleSubmit}
                type="button"
                className="px-4 py-1 text-sm text-white bg-green-500 rounded-md shadow-sm hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>{" "}

        {/* Allergies Section */}
        <div className="my-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800 md:text-2xl">Patient Allergies</h2>
          </div>

          {hasNoKnownAllergies ? (
            <div className="p-2 border border-green-200 rounded-lg shadow-sm bg-green-50">
              <div className="flex items-center justify-between">
                <div className="flex items-start w-full gap-3 md:items-center">
                  <div className="p-2 bg-green-100 rounded-full">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800">No Known Allergies</h3>
                    <p className="text-sm text-green-600">This patient has been marked as having "No Known Allergies".</p>
                  </div>
                  {hasPermission(roles, ["prescriptionWriter", "prescriptionAdmin"]) && (
                    <button
                      onClick={() => {
                        const noKnownAllergy = allergies.find(a => a.no_known_allergies);
                        if (noKnownAllergy) {
                          openEditModal(noKnownAllergy);
                        }
                      }}
                      className="p-1 h-7 px-1.5 bg-green-500 hover:bg-green-800 text-white rounded-md flex items-center gap-1 ml-auto text-xs shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <BsPencil className="inline-block text-xs text-white" />
                      Edit
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Table
              headers={allergyHeaders}
              rows={allergyRows}
              actions={allergyActions}
              className="p-2 rounded-md shadow-md"
              emptyMessage="No allergies recorded"
              headerBgColor="bg-green-200"
              showSearch={true}
              searchPlaceholder="Search allergies..."
              itemsPerPage={5}
              showDateFilter={true}
              onDateChange={({ startDate, endDate }) => {
                console.log("Date range:", { startDate, endDate });
              }}
              dateFilterProps={{
                placeholder: "Filter by onset date",
                minDate: "2000-01-01",
                maxDate: new Date().toISOString().split("T")[0],
              }}
              onAdd={() => setIsModalOpen(true)}
              addButtonText="Add Allergy"
              addButtonClassName="bg-green-500 hover:bg-green-600 text-white"
              showAddButton={hasPermission(roles, ["prescriptionWriter", "prescriptionAdmin"])}
              showTotalCount={false}
            />
          )}
        </div>

        <AddAllergyModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          patientId={patientId}
          hasNoKnownAllergies={hasNoKnownAllergies}
        />
        <EditAllergyModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          allergyToEdit={selectedAllergy}
          hasNoKnownAllergies={hasNoKnownAllergies}
        />
        {/* Prescription Table */}
        <div className="mt-8">
          <Table
            headers={prescriptionHeaders}
            rows={prescriptionRows}
            actions={prescriptionActions}
            title="Patient Prescriptions"
            className="p-2 rounded-md shadow-md"
            headerBgColor="bg-green-200"
            emptyMessage="No prescriptions found"
            itemsPerPage={5}
            loading={prescriptionsLoading}
            showAddButton={true}
            onRowClick={handleRowClick}
            rowClassName="cursor-pointer hover:bg-gray-50"
            filterOptions={prescriptionFilterOptions}
            statusConfig={prescriptionStatusConfig}
            onAdd={() => navigate('/retail/prescriptions/add-prescription', {
              state: {
                patientId,
                patientInfo: {
                  id: patientId,
                  name: `${patientDetail?.first_name || ''} ${patientDetail?.last_name || ''}`.trim(),
                  dob: patientDetail?.date_of_birth,
                  gender: patientDetail?.gender,
                  phone: patientDetail?.phone_number
                },
                requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin']
              }
            })}
            addButtonText="Add New Prescription"
            addButtonClassName="bg-green-500 hover:bg-green-600 text-white"
          />
        </div>
      </div>
    </div>
  );
};

export default ViewPatient;
